import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/services', label: t('nav.services') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/about', label: t('nav.about') },
    { to: '/testimonials', label: t('nav.testimonials') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E5E5E0]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo(0, 0)}
            data-testid="navbar-logo"
            className="font-script text-3xl md:text-4xl text-[#2C3333] hover:text-[#8DA399] transition-colors"
          >
            Galene home
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => window.scrollTo(0, 0)}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`font-sans text-sm font-medium tracking-wide transition-colors ${
                  isActive(link.to)
                    ? 'text-[#8DA399]'
                    : 'text-[#2C3333] hover:text-[#8DA399]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Toggle & Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center space-x-2 px-3 py-2 rounded-full border border-[#E5E5E0] hover:border-[#8DA399] transition-colors"
            >
              <Globe className="w-4 h-4 text-[#5C5C5C]" />
              <span className="text-sm font-medium text-[#2C3333] uppercase">
                {language}
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-[#2C3333] hover:text-[#8DA399] transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-20 left-0 right-0 bg-white border-b border-[#E5E5E0] shadow-lg animate-fade-in"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => window.scrollTo(0, 0)}
                  data-testid={`mobile-nav-link-${link.to.replace('/', '') || 'home'}`}
                  className={`block font-sans text-base font-medium py-2 transition-colors ${
                    isActive(link.to)
                      ? 'text-[#8DA399]'
                      : 'text-[#2C3333] hover:text-[#8DA399]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
                <Button
                  data-testid="mobile-cta-button"
                  className="w-full mt-4 bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
